import React, { useState } from "react";
import { FormikContext, useFormik } from "formik";
import * as Yup from "yup";
import get from "lodash.get";

import { useAuthUpdater } from "../../../../../lib/auth/context/authContext";
import useAuthSession from "../../../../../lib/auth/hooks/useAuthSession";
import { useToastUpdater } from "../../../../../context/toastContext/toastContext";
import { FetchPlusError } from "../../../../../lib/fetchPlus/fetchPlus";
import { SlideProps } from "../../types";
import userService from "../../../../../services/user.service";

import Button from "../../../../common/Buttons/Button/Button";
import Input from "../../../../common/Form/Input/Input";
import Typography from "../../../../common/Typography/Typography";
import FormikFieldError from "../../../../common/Form/Errors/FormikFieldError";
import PasswordStrength from "../../../../common/PasswordStrength/PasswordStrength";
import FormFieldError from "../../../../common/Form/Errors/FormFieldError";

const FormSchema = Yup.object().shape({
    current_password: Yup.string().required("Current password is required"),
    password: Yup.string().required("New password is required"),
    password_confirmation: Yup.string().when("password", {
        is: (val: string) => !!val,
        then: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords do not match")
            .required("Confirm new password is required"),
    }),
});

const initialValues = {
    password: "",
    password_confirmation: "",
    current_password: "",
};

const ForcePasswordForm = ({ nextSlide }: SlideProps) => {
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [apiErrors, setApiErrors] = useState<string[]>([]);

    const { session } = useAuthSession({ required: true });
    const { refreshSession } = useAuthUpdater();
    const user = session?.user;

    const { addToast } = useToastUpdater();

    const formikHook = useFormik({
        initialValues: {
            ...initialValues,
        },
        validationSchema: FormSchema,
        onSubmit: async (values, { setSubmitting }) => {
            if (!user?.id || !isPasswordValid) {
                return;
            }

            setApiErrors([]);
            setSubmitting(true);

            try {
                await userService.updateProfile({
                    firstname: user.firstname,
                    lastname: user.lastname,
                    phone: user.phone || undefined,
                    new_password: values.password,
                    new_password_confirmation: values.password_confirmation,
                    current_password: values.current_password,
                });
                await refreshSession();
                if (nextSlide) nextSlide();
            } catch (error) {
                const apiErrorArray = Object.values(get(error as FetchPlusError, "response.errors") || {}).reduce(
                    (acc, curr) => acc.concat(curr),
                    []
                );

                setApiErrors(apiErrorArray);

                addToast(`Your security settings could not be updated. Please try again.`, "error", "");
                setSubmitting(false);
            }
        },
    });

    const { isSubmitting, values, handleSubmit, handleChange } = formikHook;

    return (
        <div className="space-y-2">
            <Typography as="p" variant="meta">
                Now lets get your password changed to something more secure!
            </Typography>
            <FormikContext.Provider value={formikHook}>
                <form className="flex flex-col space-y-2" onSubmit={handleSubmit}>
                    <fieldset className="space-y-1">
                        <Input
                            required
                            id="current_password"
                            name="current_password"
                            type="password"
                            label="Current password"
                            placeholder="*******"
                            error={!!formikHook.touched.current_password && !!formikHook.errors.current_password}
                            onChange={handleChange}
                        />
                        <FormikFieldError name="current_password" />
                    </fieldset>
                    <fieldset className="space-y-1">
                        <Input
                            required
                            id="password"
                            name="password"
                            type="password"
                            label="New password"
                            placeholder="*******"
                            onChange={handleChange}
                        />
                        <FormikFieldError name="password" />
                        <PasswordStrength passwordToValidate={values.password} onChange={setIsPasswordValid} />
                    </fieldset>
                    <fieldset>
                        <Input
                            required
                            id="password_confirmation"
                            name="password_confirmation"
                            type="password"
                            label="Confirm Password"
                            placeholder="*******"
                            error={!!formikHook.touched.password_confirmation && !!formikHook.errors.password_confirmation}
                            onChange={handleChange}
                        />
                        <FormikFieldError name="password_confirmation" />
                    </fieldset>
                    {apiErrors.length ? (
                        <ul>
                            {apiErrors.map((error) => (
                                <li key={error}>
                                    <FormFieldError>{error}</FormFieldError>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <div className="flex gap-2 justify-end">
                        <Button
                            type="submit"
                            size="large"
                            icon="arrow_right"
                            loading={isSubmitting}
                            disabled={!isPasswordValid}
                            title={!isPasswordValid ? "ensure password is strong" : "Confirm these details"}
                        >
                            Confirm these details
                        </Button>
                    </div>
                </form>
            </FormikContext.Provider>
        </div>
    );
};

export default ForcePasswordForm;
